import React, { createContext, useContext, useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface BackdropContextProps {
  showBackdropManually: (open: boolean, text?: string) => void; // Allow custom text
}

const BackdropContext = createContext<BackdropContextProps | undefined>(
  undefined
);

export const BackdropProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [manualOpen, setManualOpen] = useState(false);
  const [customText, setCustomText] = useState<string | undefined>(undefined);

  const isFetching = useIsFetching(); // Tracks active fetch queries
  const isMutating = useIsMutating(); // Tracks active mutations

  // Automatically show/hide the backdrop based on queryClient activity
  const open = manualOpen || isFetching > 0 || isMutating > 0;

  const showBackdropManually = (open: boolean, text?: string) => {
    setManualOpen(open);
    setCustomText(text); // Set custom text if provided
  };

  return (
    <BackdropContext.Provider value={{ showBackdropManually }}>
      {children}
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.modal + 1 })}
        open={open}
        className="flex flex-col text-center justify-center items-center gap-3"
      >
        <CircularProgress color="inherit" />
        {customText && <p>{customText}</p>}
        {!customText && isFetching > 0 && <p>{t("general.loading")}</p>}
        {!customText && isMutating > 0 && <p>{t("general.saving")}</p>}
      </Backdrop>
    </BackdropContext.Provider>
  );
};

export const useBackdrop = () => {
  const context = useContext(BackdropContext);
  if (!context) {
    throw new Error("useBackdrop must be used within a BackdropProvider");
  }
  return context;
};
